import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Globe, ArrowRight } from "lucide-react";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back.");
      navigate("/");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex bg-[#f7fafc] text-[#1a202c] relative overflow-hidden">
      {/* Decorative teal blobs */}
      <div className="absolute -top-32 -left-24 h-96 w-96 rounded-full bg-brand opacity-20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 right-1/4 h-80 w-80 rounded-full bg-[#4fd1c5] opacity-15 blur-3xl pointer-events-none" />

      <div className="hidden lg:flex flex-1 relative overflow-hidden items-center justify-center">
        <div className="relative z-10 max-w-md p-12">
          <div className="flex items-center gap-3">
            <div className="brand-icon">
              <Globe className="h-4 w-4" strokeWidth={2.4} />
            </div>
            <div className="font-display text-lg">
              rank<span className="text-[#38b2ac]">.</span>os
            </div>
          </div>
          <div className="mt-10 space-y-5">
            <div className="text-[11px] font-mono-tight tracking-widest text-[#38b2ac] uppercase font-semibold">
              v1.0 · (Stable) Agency Edition
            </div>
            <h1 className="font-display text-5xl leading-[1.05] tracking-tight text-[#1a202c]">
              SEO command<br />
              <span className="text-[#38b2ac]">center for agencies.</span>
            </h1>
            <p className="text-[#4a5568] text-sm max-w-md leading-relaxed">
              A precise, data-dense workspace to manage client domains,
              keywords, backlinks, and rankings.
            </p>
            <div className="grid grid-cols-3 gap-3 max-w-md pt-5">
              {[
                { k: "Live SERP", v: "Real-time" },
                { k: "Backlinks", v: "8B+ db" },
                { k: "Keywords", v: "Global" },
              ].map((s) => (
                <div key={s.k} className="bg-white rounded-xl p-3 shadow-[0_2px_8px_-4px_rgba(45,55,72,0.06)]">
                  <div className="text-[9px] uppercase tracking-widest text-[#a0aec0] font-semibold">
                    {s.k}
                  </div>
                  <div className="font-mono-tight text-sm mt-1 text-[#1a202c]">{s.v}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="flex-1 flex items-center justify-center p-8 relative z-10">
        <form onSubmit={onSubmit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div className="bg-white rounded-2xl shadow-[0_8px_30px_-8px_rgba(45,55,72,0.08)] p-8">
            <div className="text-[11px] uppercase tracking-widest text-[#a0aec0] font-semibold mb-2">
              Sign in
            </div>
            <h2 className="font-display text-3xl text-[#1a202c]">Welcome back.</h2>
            <p className="text-sm text-[#718096] mt-1.5">
              Use your admin credentials to access the workspace.
            </p>

            <div className="space-y-4 mt-7">
              <div>
                <label className="text-[11px] uppercase tracking-widest text-[#718096] font-semibold">
                  Email
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="login-email"
                  className="mt-1.5 w-full bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
                  required
                />
              </div>
              <div>
                <label className="text-[11px] uppercase tracking-widest text-[#718096] font-semibold">
                  Password
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="login-password"
                  className="mt-1.5 w-full bg-white border border-[#e2e8f0] rounded-xl px-4 py-3 text-sm font-mono-tight focus:outline-none focus:border-[#4fd1c5] focus:ring-4 focus:ring-[#4fd1c5]/15 transition-all"
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit"
              className="group mt-6 w-full bg-brand text-white hover:opacity-95 disabled:opacity-50 rounded-xl px-4 py-3 text-sm font-semibold flex items-center justify-center gap-2 transition-all shadow-[0_6px_18px_-6px_rgba(56,178,172,0.55)]"
            >
              {loading ? "Signing in…" : "Sign in"}
              <ArrowRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
